import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useEditorStore, type TranslationStore } from '~/shell/Store';
import { buildTranslationsPayload } from './buildTranslationsPayload';
import { mergeUpdateTranslationsResponseIntoDraft } from './mergeUpdateTranslationsResponse';
import { getTrainingLocales, type LocaleOption } from './getTrainingLocales';
import type {
  TrainingScript,
  LocalizedField,
  TranslationUpdatePatch,
  GetTrainingScriptResponse,
  UpdateTranslationsResponse,
  AiLoadingKey
} from './types';

const TARGET_LOCALE_STORAGE_KEY = 'translation_target_locale';

export type StatusFilter = 'missing' | 'stale' | 'manual' | 'auto';
export type MediaFilter = 'hasAudio' | 'hasVideo' | 'hasImages';
export type FilterKey = StatusFilter | MediaFilter;

const getStoredTargetLocale = (): string | null => {
  try {
    return window.localStorage.getItem(TARGET_LOCALE_STORAGE_KEY);
  } catch {
    return null;
  }
};

const persistTargetLocale = (locale?: string): void => {
  try {
    if (locale) window.localStorage.setItem(TARGET_LOCALE_STORAGE_KEY, locale);
  } catch {
    // Storage may be unavailable in private browsing
  }
};

export type TranslationScriptState = {
  loading: boolean;
  scriptOriginal: TrainingScript | null;
  scriptDraft: TrainingScript | null;
  targetLocale: string | undefined;
  isSaving: boolean;
  saveError: string | null;
  aiLoading: boolean;
  aiLoadingKey: AiLoadingKey | null;
  lastSavedAt: number | null;
  availableLocales: LocaleOption[];
  effectiveTargetLocale: string;
  buildTranslationsPatch: TranslationUpdatePatch | null;
  statusText: string;
  filtersOpen: boolean;
  activeFilters: Set<FilterKey>;
};

export type TranslationScriptActions = {
  load: (overrides?: {
    targetLocale?: string;
    sequenceId?: number | 'all' | null;
    chapterId?: number | null;
  }) => Promise<void>;
  doSave: () => Promise<void> | undefined;
  updateTarget: (
    path: readonly (string | number)[],
    value: string | null | undefined
  ) => void;
  aiTranslate: (
    patch: TranslationUpdatePatch,
    loadingKey: AiLoadingKey
  ) => Promise<void>;
  changeTargetLocale: (locale: string) => Promise<void>;
  setFiltersOpen: (open: boolean | ((prev: boolean) => boolean)) => void;
  toggleFilter: (key: FilterKey) => void;
};

export type FilterHelpers = {
  fieldMatchesFilter: (field?: LocalizedField | null) => boolean;
  shouldRenderField: (
    field?: LocalizedField | null,
    mediaKind?: 'Audio' | 'Video' | 'Picture' | null
  ) => boolean;
  shouldShowTargetField: (
    source?: string | null,
    target?: string | null
  ) => boolean;
  mediaFiltersActive: boolean;
};

export type TranslationScriptHook = TranslationScriptState &
  TranslationScriptActions &
  FilterHelpers;

export function useTranslationScript(): TranslationScriptHook {
  const { apiService, training, translationScope } =
    useEditorStore() as TranslationStore;

  const [loading, setLoading] = useState(false);
  const [scriptOriginal, setScriptOriginal] = useState<TrainingScript | null>(
    null
  );
  const [scriptDraft, setScriptDraft] = useState<TrainingScript | null>(null);
  const [targetLocale, setTargetLocaleState] = useState<string | undefined>();
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiLoadingKey, setAiLoadingKey] = useState<AiLoadingKey | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState<Set<FilterKey>>(
    () => new Set()
  );

  const autosaveTimerId = useRef<number | undefined>(undefined);
  const hasCompletedInitialLoad = useRef(false);
  const activeSavePromise = useRef<Promise<void> | null>(null);
  const pendingSaveRef = useRef<(() => void) | null>(null);
  const loadRequestId = useRef(0);

  const resolvedScope = useMemo(() => {
    if (translationScope.kind === 'all') {
      return { sequenceId: null, chapterId: null, isAll: true };
    }
    if (translationScope.kind === 'chapter') {
      return {
        sequenceId: null,
        chapterId: translationScope.chapterId,
        isAll: false
      };
    }
    return {
      sequenceId: translationScope.sequenceId,
      chapterId: null,
      isAll: false
    };
  }, [translationScope]);

  const availableLocales = useMemo(
    () => getTrainingLocales(training),
    [training]
  );

  const effectiveTargetLocale = useMemo(() => {
    if (targetLocale) return targetLocale;
    if (scriptDraft?.targetLocale) return scriptDraft.targetLocale;
    return availableLocales[0]?.locale || 'en';
  }, [targetLocale, scriptDraft?.targetLocale, availableLocales]);

  const buildTranslationsPatch = useMemo(() => {
    const locale = targetLocale || scriptOriginal?.targetLocale || '';
    if (!scriptOriginal || !scriptDraft) return null;
    return buildTranslationsPayload(scriptOriginal, scriptDraft, locale);
  }, [scriptOriginal, scriptDraft, targetLocale]);

  const statusText = useMemo(() => {
    if (saveError) return 'Save failed';
    if (isSaving) return 'Saving…';
    if (buildTranslationsPatch) return 'Pending changes';
    if (lastSavedAt) return 'Saved';
    return '';
  }, [saveError, isSaving, buildTranslationsPatch, lastSavedAt]);

  const isNonEmpty = useCallback(
    (value?: string | null) => !!(value && value.trim().length > 0),
    []
  );

  const shouldShowTargetField = useCallback(
    (source?: string | null, target?: string | null) =>
      isNonEmpty(source) || isNonEmpty(target),
    [isNonEmpty]
  );

  const mediaFiltersActive =
    activeFilters.has('hasAudio') ||
    activeFilters.has('hasVideo') ||
    activeFilters.has('hasImages');

  const fieldMatchesFilter = useCallback(
    (field?: LocalizedField | null): boolean => {
      const hasAnyStatusFilter =
        activeFilters.has('missing') ||
        activeFilters.has('stale') ||
        activeFilters.has('manual') ||
        activeFilters.has('auto');

      if (!hasAnyStatusFilter) return true;

      return (
        (activeFilters.has('missing') && !!field?.missing) ||
        (activeFilters.has('stale') && !!field?.stale) ||
        (activeFilters.has('manual') && field?.source_kind === 'manual') ||
        (activeFilters.has('auto') && field?.source_kind === 'auto')
      );
    },
    [activeFilters]
  );

  const shouldRenderField = useCallback(
    (
      field?: LocalizedField | null,
      mediaKind?: 'Audio' | 'Video' | 'Picture' | null
    ): boolean => {
      if (!fieldMatchesFilter(field)) return false;
      if (!shouldShowTargetField(field?.source, field?.target)) return false;
      if (!mediaFiltersActive) return true;
      if (!mediaKind) return false;
      if (mediaKind === 'Audio') return activeFilters.has('hasAudio');
      if (mediaKind === 'Video') return activeFilters.has('hasVideo');
      if (mediaKind === 'Picture') return activeFilters.has('hasImages');
      return false;
    },
    [
      fieldMatchesFilter,
      shouldShowTargetField,
      mediaFiltersActive,
      activeFilters
    ]
  );

  const toggleFilter = useCallback((key: FilterKey) => {
    setActiveFilters(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const load = useCallback(
    async (overrides?: {
      targetLocale?: string;
      sequenceId?: number | 'all' | null;
      chapterId?: number | null;
    }) => {
      if (!apiService || !training?.id) return;

      const thisRequestId = ++loadRequestId.current;

      setLoading(true);
      setScriptOriginal(null);
      setScriptDraft(null);

      try {
        const nextSeqId =
          overrides?.sequenceId !== undefined
            ? overrides.sequenceId
            : resolvedScope.sequenceId;
        const nextChapterId =
          overrides?.chapterId !== undefined
            ? overrides.chapterId
            : resolvedScope.chapterId;

        const storedLocale = getStoredTargetLocale();
        const storedValid =
          storedLocale &&
          storedLocale !== 'en' &&
          availableLocales.some(
            localeOption => localeOption.locale === storedLocale
          );

        const effectiveTarget =
          overrides?.targetLocale ??
          targetLocale ??
          (storedValid ? storedLocale : undefined) ??
          availableLocales.find(localeOption => localeOption.locale !== 'en')
            ?.locale ??
          availableLocales[0]?.locale ??
          'en';

        const response: GetTrainingScriptResponse =
          await apiService.getTrainingScript(training.id, {
            sourceLocale: 'en',
            targetLocale: effectiveTarget,
            sequenceId: nextChapterId != null ? undefined : nextSeqId,
            chapterId: nextChapterId
          });

        if (thisRequestId !== loadRequestId.current) return;

        if (!('script' in response)) return;

        const script: TrainingScript = response.script;
        setScriptOriginal(script);
        setScriptDraft(structuredClone(script));

        if (effectiveTarget !== targetLocale) {
          setTargetLocaleState(effectiveTarget);
          persistTargetLocale(effectiveTarget);
        }
      } finally {
        if (thisRequestId === loadRequestId.current) {
          setLoading(false);
        }
      }
    },
    [apiService, training?.id, resolvedScope, availableLocales, targetLocale]
  );

  const doSave = useCallback(() => {
    if (!apiService || !training?.id || !buildTranslationsPatch) return;

    if (activeSavePromise.current) {
      return activeSavePromise.current;
    }

    const run = (async () => {
      setIsSaving(true);
      setSaveError(null);
      try {
        const response: UpdateTranslationsResponse =
          await apiService.updateTranslations(
            training.id,
            buildTranslationsPatch
          );

        if ('error' in response && response.error) {
          setSaveError(response.message || 'Failed to save translations');
        } else if (scriptDraft) {
          setScriptOriginal(structuredClone(scriptDraft));
          setLastSavedAt(Date.now());
        }
      } catch (error) {
        setSaveError(
          error instanceof Error ? error.message : 'Failed to save translations'
        );
      } finally {
        setIsSaving(false);
        activeSavePromise.current = null;
      }
    })();

    activeSavePromise.current = run;
    return run;
  }, [apiService, training?.id, buildTranslationsPatch, scriptDraft]);

  const updateTarget = useCallback(
    (path: readonly (string | number)[], value: string | null | undefined) => {
      if (!scriptDraft) return;

      const next: TrainingScript = structuredClone(scriptDraft);

      let current: Record<string, unknown> = next as unknown as Record<
        string,
        unknown
      >;
      for (let i = 0; i < path.length - 1; i++) {
        const child = current[path[i] as string];
        if (!child || typeof child !== 'object') return;
        current = child as Record<string, unknown>;
      }

      const field = current[path[path.length - 1] as string] as
        | LocalizedField
        | undefined;
      if (!field) return;

      field.target = value ?? '';
      field.missing = !field.target.trim();
      if (field.stale !== undefined) field.stale = false;

      setScriptDraft(next);
    },
    [scriptDraft]
  );

  const aiTranslate = useCallback(
    async (patch: TranslationUpdatePatch, loadingKey: AiLoadingKey) => {
      if (!apiService || !training?.id) return;

      setAiLoading(true);
      setAiLoadingKey(loadingKey);

      try {
        const payload: TranslationUpdatePatch = {
          ai_translate: true,
          ...patch
        };
        const response: UpdateTranslationsResponse =
          await apiService.updateTranslations(training.id, payload);

        if ('error' in response && response.error) {
          setSaveError(response.message || 'Auto translation failed');
        } else {
          const responsePatch: TranslationUpdatePatch = response;

          setScriptOriginal(currentOriginal => {
            if (!currentOriginal) return currentOriginal;
            const next = structuredClone(currentOriginal);
            mergeUpdateTranslationsResponseIntoDraft(
              next,
              responsePatch,
              effectiveTargetLocale
            );
            return next;
          });

          setScriptDraft(currentDraft => {
            if (!currentDraft) return currentDraft;
            const next = structuredClone(currentDraft);
            mergeUpdateTranslationsResponseIntoDraft(
              next,
              responsePatch,
              effectiveTargetLocale
            );
            return next;
          });
        }
      } catch (error) {
        setSaveError(
          error instanceof Error ? error.message : 'Auto translation failed'
        );
      } finally {
        setAiLoading(false);
        setAiLoadingKey(null);
      }
    },
    [apiService, training?.id, effectiveTargetLocale]
  );

  const lastLoadedTrainingId = useRef<number | null>(null);

  useEffect(() => {
    if (!apiService || !training?.id) return;
    if (lastLoadedTrainingId.current === training.id) return;
    lastLoadedTrainingId.current = training.id;
    load();
  }, [apiService, training?.id, load]);

  useEffect(() => {
    if (scriptOriginal && scriptDraft) {
      hasCompletedInitialLoad.current = true;
    }
  }, [scriptOriginal, scriptDraft]);

  useEffect(() => {
    if (!hasCompletedInitialLoad.current) return;
    if (loading) return;
    if (!buildTranslationsPatch) return;
    if (isSaving) return;

    if (autosaveTimerId.current) {
      clearTimeout(autosaveTimerId.current);
    }

    autosaveTimerId.current = window.setTimeout(() => {
      doSave();
    }, 1000);

    pendingSaveRef.current = buildTranslationsPatch ? doSave : null;

    return () => {
      if (autosaveTimerId.current) {
        clearTimeout(autosaveTimerId.current);
      }
    };
  }, [scriptDraft, loading, isSaving, buildTranslationsPatch, doSave]);

  useEffect(() => {
    return () => {
      if (pendingSaveRef.current) {
        pendingSaveRef.current();
      }
    };
  }, []);

  const previousScope = useRef<typeof resolvedScope | null>(null);

  useEffect(() => {
    if (!hasCompletedInitialLoad.current) return;
    if (!apiService || !training?.id) return;

    const hasScopeChanged =
      previousScope.current &&
      (previousScope.current.sequenceId !== resolvedScope.sequenceId ||
        previousScope.current.chapterId !== resolvedScope.chapterId ||
        previousScope.current.isAll !== resolvedScope.isAll);

    previousScope.current = resolvedScope;

    if (!hasScopeChanged) return;

    const saveAndReload = async () => {
      if (buildTranslationsPatch) {
        await doSave();
      }
      await load();
    };

    void saveAndReload();
  }, [
    resolvedScope,
    apiService,
    training?.id,
    buildTranslationsPatch,
    doSave,
    load
  ]);

  useEffect(() => {
    if (!filtersOpen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setFiltersOpen(false);
      }
    };

    const onClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('[data-filter-dropdown]')) {
        setFiltersOpen(false);
      }
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('click', onClickOutside, true);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('click', onClickOutside, true);
    };
  }, [filtersOpen]);

  return {
    loading,
    scriptOriginal,
    scriptDraft,
    targetLocale,
    isSaving,
    saveError,
    aiLoading,
    aiLoadingKey,
    lastSavedAt,
    availableLocales,
    effectiveTargetLocale,
    buildTranslationsPatch,
    statusText,
    filtersOpen,
    activeFilters,
    load,
    doSave,
    updateTarget,
    aiTranslate,
    changeTargetLocale: async (locale: string) => {
      setTargetLocaleState(locale);
      persistTargetLocale(locale);
      if (buildTranslationsPatch) {
        await doSave();
      }
      await load({ targetLocale: locale });
    },
    setFiltersOpen,
    toggleFilter,
    fieldMatchesFilter,
    shouldRenderField,
    shouldShowTargetField,
    mediaFiltersActive
  };
}
